
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

interface SubscriptionStatus {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  loading: boolean;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    subscribed: false,
    subscription_tier: null,
    subscription_end: null,
    loading: true
  });

  const checkSubscription = async () => {
    if (!user) {
      setSubscriptionStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // First try to get from profiles table (faster)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_status, subscription_end')
        .eq('id', user.id)
        .single();

      if (profileData && !profileError) {
        const isSubscribed = profileData.subscription_status === 'active' && 
                            profileData.subscription_tier && 
                            profileData.subscription_tier !== 'free';
        
        setSubscriptionStatus({
          subscribed: isSubscribed,
          subscription_tier: profileData.subscription_tier,
          subscription_end: profileData.subscription_end,
          loading: false
        });
        return;
      }

      // Fallback to check-subscription function if profiles data is not available
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;
      
      setSubscriptionStatus({
        subscribed: data.subscribed || false,
        subscription_tier: data.subscription_tier || null,
        subscription_end: data.subscription_end || null,
        loading: false
      });
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const openCustomerPortal = async () => {
    if (!user) {
      toast.error('Please sign in to manage your subscription');
      return;
    }

    // Check if user has a subscription first
    if (!subscriptionStatus.subscribed) {
      toast.error('No active subscription found. Please subscribe first to manage your subscription.');
      return;
    }

    try {
      toast.loading('Opening subscription management...', { id: 'customer-portal' });
      
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) {
        console.error('Customer portal error:', error);
        
        // Handle specific error cases
        let errorMessage = 'Failed to open subscription management. Please try again.';
        
        if (error.message?.includes('No Stripe customer found')) {
          errorMessage = 'No subscription found. Please subscribe first to manage your subscription.';
        } else if (error.message?.includes('Authentication')) {
          errorMessage = 'Please sign in again to manage your subscription.';
        } else if (error.message?.includes('No configuration provided')) {
          errorMessage = 'Subscription management is temporarily unavailable. Please contact support.';
        }
        
        toast.error(errorMessage, { id: 'customer-portal' });
        return;
      }
      
      // Handle the special case where portal is not configured
      if (data?.error === 'PORTAL_NOT_CONFIGURED') {
        toast.error('Subscription management is being set up. Please contact support for now.', { id: 'customer-portal' });
        console.log('Available subscription details:', data.subscriptions);
        return;
      }
      
      if (data?.url) {
        toast.success('Opening subscription management...', { id: 'customer-portal' });
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open subscription management. Please try again.', { id: 'customer-portal' });
    }
  };

  useEffect(() => {
    checkSubscription();
  }, [user]);

  return {
    ...subscriptionStatus,
    checkSubscription,
    openCustomerPortal
  };
};
